const object = value => value !== null && typeof value === "object" && !Array.isArray(value);

export function strictNumber(value) {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function eventTime(value) {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    const ms = value < 1e12 ? value * 1000 : value;
    return Number.isFinite(new Date(ms).getTime()) ? ms : null;
  }
  if (typeof value !== "string" || !value.trim()) return null;
  if (/^\d{10}(\.\d+)?$|^\d{13}$/.test(value)) return eventTime(Number(value));
  // Do not interpret a database wall-clock string as an absolute instant.
  // Confirm backend offset/timezone semantics before adapting such responses.
  if (!/T.*(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function decoded(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    throw new Error("A monitor log contains invalid JSON.");
  }
}

export function reportPayload(row) {
  if (!object(row)) throw new Error("A monitor log row is invalid.");
  const candidates = [row.data, row.value, row.device_value, row];
  for (const candidate of candidates) {
    const value = decoded(candidate);
    if (object(value) && value.channels) return value;
    if (object(value?.device_value) && value.device_value.channels) return value.device_value;
  }
  return null;
}

export function channelEntries(channels) {
  if (Array.isArray(channels)) {
    return channels.filter(object).map(raw => {
      const key = raw.channel_number ?? raw.channel;
      if (key == null) throw new Error("A historical channel array entry has no channel ID.");
      return [String(key), raw];
    });
  }
  if (!object(channels)) return [];
  return Object.entries(channels).filter(([, raw]) => object(raw))
    .map(([key, raw]) => [String(raw.channel_number ?? raw.channel ?? key), raw]);
}

export function channelSample(raw, kind) {
  if (raw.is_error) return { value: null, unit: "" };
  const unit = raw.state?.unit || raw.display_unit || raw.canonical_unit || "";
  if (kind === "level") {
    const explicit = strictNumber(raw.percent_level) ?? strictNumber(raw.fill_percentage);
    if (explicit !== null) return { value: explicit, unit: "%" };
    const current = /^(%|percent)$/i.test(String(unit).trim()) ? strictNumber(raw.current_value) : null;
    return { value: current, unit: "%" };
  }
  return {
    value: strictNumber(raw.display_value) ?? strictNumber(raw.current_value),
    unit: String(unit),
  };
}

export function rowsForChannel(rows, channelKey, kind, expectedUnit) {
  const points = [];
  const issues = { missingTime: 0, missingValue: 0, incompatibleUnit: 0 };
  for (const row of rows) {
    const payload = reportPayload(row);
    if (!payload) continue; // Valid non-channel reports do not fabricate points.
    const match = channelEntries(payload.channels).find(([key]) => key === String(channelKey));
    if (!match) continue;
    const raw = match[1];
    const candidates = [raw.timestamp, raw.updated_at, payload.timestamp, row.timestamp, row.created_at];
    const first = candidates.find(value => value != null && value !== "");
    const t = eventTime(first);
    if (t === null) { issues.missingTime++; continue; }
    const { value, unit } = channelSample(raw, kind);
    if (value === null) { issues.missingValue++; continue; }
    // No implicit Fahrenheit/Celsius/pressure conversion. Confirm units.
    if (expectedUnit && unit !== expectedUnit) { issues.incompatibleUnit++; continue; }
    points.push({
      t, v: value, unit, reportId: row.id ?? null,
      receivedAt: eventTime(row.created_at), raw, row,
    });
  }
  const unique = new Map();
  for (const point of points) {
    // Deduplicate REST/live copies of the same channel sample without
    // collapsing distinct values at the same timestamp.
    const key = JSON.stringify([String(channelKey), point.t, point.v, point.unit]);
    const previous = unique.get(key);
    if (!previous || previous.reportId == null && point.reportId != null) unique.set(key, point);
  }
  return { points: [...unique.values()].sort((a, b) => a.t - b.t), issues };
}

export function mergeReportRows(previous, incoming) {
  if (!Array.isArray(incoming)) throw new Error("Monitor logs did not return an array.");
  const byId = new Map();
  for (const row of [...previous, ...incoming]) {
    if (!object(row)) throw new Error("A monitor log row is invalid.");
    const id = row.id == null ? null : String(row.id);
    const payload = reportPayload(row);
    if (id == null && !payload) throw new Error("An unidentified monitor log has no payload.");
    const key = id == null
      ? JSON.stringify(["event", payload?.timestamp ?? row.timestamp ?? row.created_at, payload])
      : `id:${id}`;
    byId.set(key, row);
  }
  return [...byId.values()];
}

export function pointsInWindow(points, from, end) {
  if (!Number.isFinite(from) || !Number.isFinite(end) || from >= end) {
    throw new Error("Choose a valid history window.");
  }
  // (from, end] assigns exact-boundary samples to only one adjacent window.
  return points.filter(point => point.t > from && point.t <= end);
}
